import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Pencil, 
  Plus, 
  Trash2, 
  GripVertical, 
  Save, 
  X,
  AlertCircle,
  Check
} from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface Question {
  id: string;
  text: string;
  description?: string;
  required: boolean;
  order: number;
  category?: string;
  isCustom?: boolean;
}

interface QuestionManagerProps {
  configItemId?: string;
  companyId?: string;
  category: string;
  templateId?: string;
  accessToken: string;
  mode?: 'global' | 'config-item' | 'template';
  onQuestionsUpdated?: (questions: Question[]) => void;
}

export function QuestionManager({
  configItemId,
  companyId,
  category,
  templateId,
  accessToken,
  mode = 'config-item',
  onQuestionsUpdated,
}: QuestionManagerProps) {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, [category, configItemId, templateId]);

  const loadQuestions = async () => {
    try {
      setLoading(true);

      let url = '';
      if (mode === 'config-item' && configItemId && companyId) {
        url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}/questions`;
      } else if (mode === 'template' && templateId) {
        url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/questions/${templateId}/${category}`;
      } else {
        url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/questions/global/${category}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions || []);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const saveQuestions = async () => {
    try {
      setSaving(true);

      let url = '';
      if (mode === 'config-item' && configItemId && companyId) {
        url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}/questions`;
      } else if (mode === 'template' && templateId) {
        url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/questions/${templateId}/${category}`;
      } else {
        url = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/questions/global/${category}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ questions }),
      });

      if (!response.ok) {
        throw new Error('Failed to save questions');
      }

      toast.success('Questions saved successfully');
      
      if (onQuestionsUpdated) {
        onQuestionsUpdated(questions);
      }
    } catch (error) {
      console.error('Error saving questions:', error);
      toast.error('Failed to save questions');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: `custom_${Date.now()}`,
      text: '',
      description: '',
      required: false,
      order: questions.length,
      category,
      isCustom: true,
    };
    setEditingQuestion(newQuestion);
    setShowEditDialog(true);
  };

  const editQuestion = (question: Question) => {
    setEditingQuestion({ ...question });
    setShowEditDialog(true);
  };

  const deleteQuestion = (questionId: string) => {
    setQuestions(questions.filter(q => q.id !== questionId));
    toast.success('Question deleted');
  };

  const saveEditedQuestion = () => {
    if (!editingQuestion) return;

    if (!editingQuestion.text.trim()) {
      toast.error('Question text is required');
      return;
    }

    const existingIndex = questions.findIndex(q => q.id === editingQuestion.id);
    
    if (existingIndex >= 0) {
      // Update existing
      const updated = [...questions];
      updated[existingIndex] = editingQuestion;
      setQuestions(updated);
    } else {
      // Add new
      setQuestions([...questions, editingQuestion]);
    }

    setShowEditDialog(false);
    setEditingQuestion(null);
    toast.success('Question updated');
  };

  const moveQuestion = (dragIndex: number, hoverIndex: number) => {
    const draggedQuestion = questions[dragIndex];
    const updated = [...questions];
    updated.splice(dragIndex, 1);
    updated.splice(hoverIndex, 0, draggedQuestion);
    // Update order
    updated.forEach((q, idx) => q.order = idx);
    setQuestions(updated);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">Loading questions...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Question Manager</CardTitle>
              <CardDescription>
                {mode === 'config-item' && 'Customize questions for this config item'}
                {mode === 'template' && 'Manage template questions'}
                {mode === 'global' && 'Manage global question library'}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={addQuestion}>
                <Plus className="w-4 h-4 mr-2" />
                Add Question
              </Button>
              <Button onClick={saveQuestions} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save All'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {questions.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500 mb-4">No questions yet</p>
              <Button onClick={addQuestion}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Question
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {questions
                .sort((a, b) => a.order - b.order)
                .map((question, index) => (
                  <DraggableQuestion
                    key={question.id}
                    question={question}
                    index={index}
                    onEdit={() => editQuestion(question)}
                    onDelete={() => deleteQuestion(question.id)}
                    onMove={moveQuestion}
                  />
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Question Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion?.id?.startsWith('custom_') ? 'Add Question' : 'Edit Question'}
            </DialogTitle>
            <DialogDescription>
              Configure the question that will be asked during discovery
            </DialogDescription>
          </DialogHeader>

          {editingQuestion && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Question Text <span className="text-red-500">*</span>
                </label>
                <Input
                  value={editingQuestion.text}
                  onChange={(e) =>
                    setEditingQuestion({ ...editingQuestion, text: e.target.value })
                  }
                  placeholder="What information do you need?"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Description/Help Text
                </label>
                <Textarea
                  value={editingQuestion.description || ''}
                  onChange={(e) =>
                    setEditingQuestion({ ...editingQuestion, description: e.target.value })
                  }
                  placeholder="Additional context or examples to help answer this question"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={editingQuestion.required}
                  onChange={(e) =>
                    setEditingQuestion({ ...editingQuestion, required: e.target.checked })
                  }
                  className="rounded"
                />
                <label htmlFor="required" className="text-sm font-medium cursor-pointer">
                  Required question (must be answered)
                </label>
              </div>

              {editingQuestion.isCustom && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-medium">Custom Question</p>
                      <p className="mt-1">
                        This is a custom question you've added. It will only appear for this{' '}
                        {mode === 'config-item' ? 'config item' : mode}.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setEditingQuestion(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={saveEditedQuestion}>
              <Check className="w-4 h-4 mr-2" />
              Save Question
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DndProvider>
  );
}

// Draggable question component
interface DraggableQuestionProps {
  question: Question;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
}

function DraggableQuestion({
  question,
  index,
  onEdit,
  onDelete,
  onMove,
}: DraggableQuestionProps) {
  const [{ isDragging }, drag] = useDrag({
    type: 'QUESTION',
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'QUESTION',
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        onMove(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`flex items-center gap-3 p-3 bg-white border rounded-lg hover:border-gray-400 transition-colors ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <GripVertical className="w-5 h-5 text-gray-400 cursor-move flex-shrink-0" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium">{question.text}</span>
          {question.required && (
            <Badge variant="secondary" className="text-xs">
              Required
            </Badge>
          )}
          {question.isCustom && (
            <Badge variant="outline" className="text-xs">
              Custom
            </Badge>
          )}
        </div>
        {question.description && (
          <p className="text-sm text-gray-500">{question.description}</p>
        )}
      </div>

      <div className="flex gap-1 flex-shrink-0">
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}